import React from 'react';
import { useLocation } from 'react-router-dom';
import { sortBy } from 'ramda';
import ArrowUpIcon from '@moda/icons/arrow-up-12';
import { PageFilterFiltersFragment } from '../../generated/types/PageFilterFiltersFragment';
import { PageFilterFragment } from '../../generated/types/PageFilterFragment';
import { findRouteFor } from '../../routers';
import { JEWELRY_CATEGORIES } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCategoryTree } from '../../hooks/useCategoryTree';
import { Rotate } from '../Rotate';
import { PageFilterFacets } from './PageFilterFacets';
import { PageFilterLink } from './PageFilterLink';
import { PageFilterPanel } from './PageFilterPanel';
import { PageFilterJewelryCategory } from './PageFilterJewelryCategory';

import './PageFilterCategory.scss';

type PageFilterCategoryProps = {
  attributes: PageFilterFiltersFragment['attributes'];
  categoryTree: PageFilterFragment['categoryTree'];
  mobile?: boolean;
  showCounts?: boolean;
  onClose: () => void;
};

export const PageFilterCategory: React.FC<PageFilterCategoryProps> = ({
  attributes,
  categoryTree,
  mobile = false,
  showCounts = true,
  onClose
}) => {
  const {
    params: { category, subcategory, section }
  } = usePLPParams();
  const location = useLocation();
  const route = findRouteFor(location.pathname);

  const {
    selectedCategoryNode,
    activeCategoryNode,
    parentCategoryNode,
    parentCategoryName,
    isTopLevel,
    getCategoryNodeHref
  } = useCategoryTree(categoryTree, (attributes?.length || 0) > 0);

  if (
    route?.key === 'ProductCategoryListingPage' &&
    !section &&
    category &&
    JEWELRY_CATEGORIES.includes(category) &&
    !(subcategory && attributes?.length)
  )
    return (
      <PageFilterJewelryCategory
        categoryTree={categoryTree}
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
      />
    );

  const title = activeCategoryNode?.name || 'Category';
  const isSubCategory =
    subcategory && subcategory.toLowerCase() === title.toLowerCase().replace(/[^a-z]+/g, '-');

  const content =
    attributes?.length && subcategory ? (
      <PageFilterFacets
        facets={attributes}
        facetName="attributes"
        mobile={mobile}
        showCounts={showCounts}
        onClose={onClose}
        title={mobile ? 'Category' : activeCategoryNode?.name}
        sortingBy={facet =>
          isSubCategory ? -(facet.totalItems as number) : (facet.name as string)
        }
      />
    ) : (
      <>
        {!isTopLevel && !mobile && <h4 className="PageFilterCategory__title">{title}</h4>}

        {sortBy(category => category?.name || '', activeCategoryNode?.children || []).map(
          category =>
            category &&
            category.slug &&
            category.slug !== 'sale' && (
              <PageFilterLink
                selected={selectedCategoryNode === category}
                key={category.slug}
                to={getCategoryNodeHref(category) || ''}
                onClick={onClose}
              >
                <span>{category.name}</span>

                {showCounts && <span>{category.totalItems}</span>}
              </PageFilterLink>
            )
        )}
      </>
    );

  if (mobile && attributes?.length && subcategory) return content;

  if (mobile) {
    return (
      <PageFilterPanel onClose={onClose} title="Category">
        {content}
      </PageFilterPanel>
    );
  }

  return (
    <div className="PageFilterCategory">
      {isTopLevel ? (
        <>
          <h4 className="PageFilterCategory__title">{title}</h4>

          <PageFilterLink
            selected={(activeCategoryNode.children || []).every(
              child => child !== selectedCategoryNode
            )}
            to={getCategoryNodeHref(activeCategoryNode) || ''}
          >
            <span>All {activeCategoryNode?.name || 'Results'}</span>

            {activeCategoryNode.totalItems ?? <span>{activeCategoryNode.totalItems}</span>}
          </PageFilterLink>
        </>
      ) : (
        <PageFilterLink
          aria-label={`Go Back to ${parentCategoryName}`}
          back
          to={parentCategoryNode ? getCategoryNodeHref(parentCategoryNode) || '' : ''}
        >
          <Rotate degrees={-90}>
            <ArrowUpIcon />
          </Rotate>

          {parentCategoryName}
        </PageFilterLink>
      )}

      {content}
    </div>
  );
};
